import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const UploadProgressContext = createContext();

export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error('useUploadProgress must be used within UploadProgressProvider');
  }
  return context;
};

export const UploadProgressProvider = ({ children }) => {
  const [progressData, setProgressData] = useState({
    isUploading: false,
    currentStep: 0,
    totalSteps: 0,
    stepName: '',
    percentage: 0,
    error: null,
  });

  const lastUpdateRef = useRef(0);
  const THROTTLE_MS = 16;

  const startUpload = (totalSteps, initialStepName = 'Starting...') => {
    setProgressData({
      isUploading: true,
      currentStep: 0,
      totalSteps,
      stepName: initialStepName,
      percentage: 0,
      error: null,
    });
  };

  const updateProgress = useCallback((currentStep, stepName, percentage = null) => {
    const now = Date.now();

    if (percentage !== 100 && now - lastUpdateRef.current < THROTTLE_MS) {
      return;
    }

    lastUpdateRef.current = now;

    const newData = {
      currentStep,
      stepName,
      percentage: percentage !== null ? percentage : Math.round((currentStep / progressData.totalSteps) * 100),
      error: null,
    };

    setProgressData(prev => ({
      ...prev,
      ...newData
    }));
  }, [progressData.totalSteps, progressData.percentage]);

  const finishUpload = () => {
    setProgressData(prev => ({
      ...prev,
      isUploading: false,
      currentStep: prev.totalSteps,
      percentage: 100,
      stepName: 'Completed',
    }));

    setTimeout(() => {
      setProgressData({
        isUploading: false,
        currentStep: 0,
        totalSteps: 0,
        stepName: '',
        percentage: 0,
        error: null,
      });
    }, 1000);
  };

  const setError = (error) => {
    setProgressData(prev => ({
      ...prev,
      isUploading: false,
      error,
    }));
  };

  const resetProgress = () => {
    setProgressData({
      isUploading: false,
      currentStep: 0,
      totalSteps: 0,
      stepName: '',
      percentage: 0,
      error: null,
    });
  };

  const value = {
    ...progressData,
    startUpload,
    updateProgress,
    finishUpload,
    setError,
    resetProgress,
  };

  return (
    <UploadProgressContext.Provider value={value}>
      {children}
    </UploadProgressContext.Provider>
  );
};